import type {
  AspectReaction,
  CommentPlatform,
  StanceCounts,
} from "~/ui-rtk/api/commentModerationApi"
import { SERIES_COLORS } from "~/ui-rtk/pages/BudgetRecommender/components/HistoryChart"

/**
 * Shared pieces of the "how people reacted" read, used by the ads table on
 * the report page and by the per-ad drawer.
 *
 * One fixed colour per stance, from the app's validated chart palette:
 * positive green, question blue, neutral muted, negative orange-red. The
 * order in every stacked bar is positive → question → neutral → negative so
 * the two poles sit at the ends and adjacent pairs stay distinguishable.
 */
export const STANCE_SERIES: Array<{
  key: keyof StanceCounts
  label: string
  color: string
}> = [
  { key: "positive", label: "Positive", color: SERIES_COLORS[2] },
  { key: "question", label: "Question", color: SERIES_COLORS[0] },
  { key: "neutral", label: "Neutral", color: "#677489" },
  { key: "negative", label: "Negative", color: SERIES_COLORS[1] },
]

const ASPECT_LABEL: Record<string, string> = {
  TASTE_QUALITY: "Taste / quality",
  PRICE: "Price",
  EFFECTIVENESS: "Effectiveness",
  HEALTH_SAFETY: "Health / safety",
  SHIPPING_DELIVERY: "Shipping",
  CUSTOMER_SERVICE: "Customer service",
  USAGE_HOWTO: "How to use",
  AVAILABILITY: "Availability",
  FOUNDER_BRAND: "Founder / brand",
  AD_CREATIVE: "The ad itself",
  COMPETITOR: "Competitor",
  OTHER: "Other",
}

export const aspectLabel = (aspect: string) => ASPECT_LABEL[aspect] ?? aspect

export const platformLabel = (platform: CommentPlatform) =>
  platform === "FACEBOOK" ? "Facebook" : "Instagram"

export function stanceTotal(c: StanceCounts): number {
  return c.positive + c.question + c.neutral + c.negative
}

export function stanceTitle(c: StanceCounts): string {
  return `${c.positive} positive · ${c.question} questions · ${c.neutral} neutral · ${c.negative} negative (${stanceTotal(c)} judged)`
}

/** A 100%-stacked stance bar with a 2px surface gap between segments. */
export function StanceBar({
  counts,
  title,
}: {
  counts: StanceCounts
  title?: string
}) {
  const total = stanceTotal(counts)
  if (total === 0) return <div className="h-2 rounded-full bg-white/5" />
  return (
    <div
      className="flex h-2 w-full gap-[2px] overflow-hidden rounded-full"
      title={title}
    >
      {STANCE_SERIES.filter(s => counts[s.key] > 0).map(s => (
        <div
          key={s.key}
          style={{
            width: `${(counts[s.key] / total) * 100}%`,
            backgroundColor: s.color,
          }}
          title={`${s.label}: ${counts[s.key]}`}
        />
      ))}
    </div>
  )
}

export function Legend() {
  return (
    <div className="flex flex-wrap gap-3 text-xs text-system-gray-400">
      {STANCE_SERIES.map(s => (
        <span key={s.key} className="flex items-center gap-1">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: s.color }}
          />
          {s.label}
        </span>
      ))}
    </div>
  )
}

/** "+0.42" / "−0.10" with a tone; the verdict of the room in one number. */
export function ScoreBadge({ score }: { score: number | null }) {
  if (score == null) {
    return <span className="text-xs text-system-gray-500">no stance</span>
  }
  let cls = "bg-system-gray-500/20 text-system-gray-300"
  if (score >= 0.2) cls = "bg-emerald-500/20 text-emerald-300"
  else if (score <= -0.2) cls = "bg-rose-500/20 text-rose-300"
  let sign = ""
  if (score > 0) sign = "+"
  else if (score < 0) sign = "−"
  return (
    <span
      className={`rounded-full px-2 py-0.5 font-mono text-xs ${cls}`}
      title="(positive − negative) ÷ judged comments"
    >
      {sign}
      {Math.abs(score).toFixed(2)}
    </span>
  )
}

export function AspectRows({ aspects }: { aspects: AspectReaction[] }) {
  const max = aspects[0]?.total ?? 0
  if (aspects.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-system-gray-500">
        No aspects yet.
      </p>
    )
  }
  return (
    <ul className="flex flex-col gap-2">
      {aspects.map(a => (
        <li key={a.aspect} className="flex items-center gap-3 text-sm">
          <div className="w-32 shrink-0 truncate text-system-gray-200">
            {aspectLabel(a.aspect)}
          </div>
          <div
            className="flex-1"
            style={{ maxWidth: `${max > 0 ? (a.total / max) * 100 : 0}%` }}
          >
            <StanceBar counts={a} />
          </div>
          <div className="w-10 shrink-0 text-right font-mono text-system-gray-100">
            {a.total}
          </div>
        </li>
      ))}
    </ul>
  )
}

export function QuoteList({
  label,
  quotes,
  cls,
  size = "sm",
}: {
  label: string
  quotes: string[]
  cls: string
  /** "md" reads at 14px, for dialogs; "sm" is the table and drawer size. */
  size?: "sm" | "md"
}) {
  if (quotes.length === 0) return null
  const labelCls = size === "md" ? "text-[12px]" : "text-xs"
  const bodyCls = size === "md" ? "text-[14px]" : "text-sm"
  return (
    <div>
      <div className={`${labelCls} uppercase tracking-wide ${cls}`}>
        {label}
      </div>
      <ul
        className={`mt-1 flex flex-col gap-1 ${bodyCls} text-system-gray-300`}
      >
        {quotes.map((q, i) => (
          <li key={`${i}-${q.slice(0, 24)}`}>“{q}”</li>
        ))}
      </ul>
    </div>
  )
}
